import { useEffect, useRef, useState, type CSSProperties, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { Country, Match, Venue } from '@/types/domain';
import { PosterCard } from '@/components/PosterCard';
import { ShareCard } from '@/components/ShareCard';
import { EVENTS, track } from '@/lib/analytics';
import { haptics } from '@/lib/native';
import {
  canNativeShare,
  imageExists,
  posterUrlFor,
  renderPosterCardToFile,
  renderShareCardToFile,
  sendShare,
} from '@/utils/shareCard';

/**
 * The single share flow for "I'm going to {venue}" plans. Drive it from any
 * screen: hold one piece of state, set it to a request to begin, clear it in
 * `onClose`. The flow captures the ShareCard (and the AI poster composite when
 * one exists) to PNGs, shows a swipeable preview, and hands the chosen image
 * to the native share sheet.
 *
 * Keep `request` set for the whole flow — capture, preview, and share all run
 * off it. `onClose` fires on cancel, completed share, or capture failure.
 */

export interface SharePlanRequest {
  match: Match;
  venue: Venue;
  venueCountry: Country;
  homeCountry: Country | undefined;
  awayCountry: Country | undefined;
}

interface SharePlanFlowProps {
  request: SharePlanRequest | null;
  onClose: () => void;
}

// Hide the ShareCard via a 0x0 overflow-hidden window rather than opacity
// (which propagates to children and produces a black capture) or extreme
// off-screen left (which breaks html-to-image's bounding-rect math). The
// inner card stays at full 1080x1920 with (0,0)-anchored coords, only its
// top-left pixel is "visible" in the clipped window.
const SHARE_HIDDEN_WRAPPER_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 0,
};

const MODAL_BACKDROP_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.88)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 24,
  // Honor iOS safe areas so the modal doesn't sit under the home indicator.
  paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
  paddingTop: 'max(24px, env(safe-area-inset-top))',
};

const MODAL_LABEL_STYLE: CSSProperties = {
  color: '#9a9590',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  fontFamily: 'ui-monospace, SF Mono, monospace',
  marginBottom: 14,
};

const PREVIEW_IMG_STYLE: CSSProperties = {
  width: 'min(280px, 70vw)',
  aspectRatio: '9 / 16',
  borderRadius: 18,
  boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
  display: 'block',
  objectFit: 'cover',
};

const SWIPE_CONTAINER_STYLE: CSSProperties = {
  display: 'flex',
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  width: 'min(280px, 70vw)',
  aspectRatio: '9 / 16',
  borderRadius: 18,
  boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',
};

const SWIPE_SLIDE_STYLE: CSSProperties = {
  flexShrink: 0,
  width: '100%',
  height: '100%',
  scrollSnapAlign: 'center',
  objectFit: 'cover',
  display: 'block',
};

const SWIPE_DOTS_STYLE: CSSProperties = {
  display: 'flex',
  gap: 8,
  marginTop: 14,
  justifyContent: 'center',
};

const SWIPE_DOT_STYLE: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 4,
  background: 'rgba(255,255,255,0.35)',
  transition: 'background 0.2s',
};

const SWIPE_DOT_ACTIVE_STYLE: CSSProperties = {
  ...SWIPE_DOT_STYLE,
  background: '#ffffff',
};

const MODAL_BUTTONS_STYLE: CSSProperties = {
  display: 'flex',
  gap: 10,
  marginTop: 22,
  width: 'min(280px, 70vw)',
};

const PRIMARY_BTN_STYLE: CSSProperties = {
  flex: 1,
  padding: '14px 0',
  borderRadius: 12,
  border: 'none',
  background: '#fff',
  color: '#0a0a0a',
  fontSize: 15,
  fontWeight: 800,
  cursor: 'pointer',
  fontFamily: '-apple-system, system-ui',
  letterSpacing: 0.2,
};

const SECONDARY_BTN_STYLE: CSSProperties = {
  flex: 1,
  padding: '14px 0',
  borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.25)',
  background: 'transparent',
  color: '#fff',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: '-apple-system, system-ui',
};

interface Preview {
  customUrl: string;
  customFile: File;
  /** Composite poster blob URL + File — raw AI image PLUS the venue claim
   *  overlay. Present only when an AI poster exists and capture succeeded. */
  posterUrl: string | undefined;
  posterFile: File | undefined;
}

export function SharePlanFlow({ request, onClose }: SharePlanFlowProps) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<Preview | null>(null);
  // 0 = AI poster (when present), 1 = custom card.
  const [selectedSlide, setSelectedSlide] = useState<0 | 1>(0);
  const shareRef = useRef<HTMLDivElement>(null);
  const posterRef = useRef<HTMLDivElement>(null);

  // Stable close: revoke blob URLs then notify the parent (which clears
  // `request`). Held in a ref so effects don't need it in their dep arrays.
  const closeRef = useRef<() => void>(() => {});
  closeRef.current = () => {
    setPreview((p) => {
      if (p) {
        URL.revokeObjectURL(p.customUrl);
        if (p.posterUrl) URL.revokeObjectURL(p.posterUrl);
      }
      return null;
    });
    onClose();
  };

  // Capture: when a request arrives, wait for the hidden cards to commit to
  // the DOM, then walk them with html-to-image into shareable PNGs.
  useEffect(() => {
    if (!request) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      // Two RAFs so the hidden ShareCard / PosterCard have committed before
      // html-to-image walks them.
      await new Promise<void>((r) =>
        requestAnimationFrame(() => requestAnimationFrame(() => r())),
      );
      if (cancelled || !shareRef.current) return;
      try {
        const candidatePosterUrl = posterUrlFor(request.match);
        // Capture custom card and probe for the poster in parallel.
        const [customFile, hasPoster] = await Promise.all([
          renderShareCardToFile(shareRef.current, request.match, request.venue),
          candidatePosterUrl ? imageExists(candidatePosterUrl) : Promise.resolve(false),
        ]);
        if (cancelled) return;
        const customUrl = URL.createObjectURL(customFile);

        let posterFile: File | undefined;
        let posterUrl: string | undefined;
        if (hasPoster && candidatePosterUrl && posterRef.current) {
          try {
            posterFile = await renderPosterCardToFile(
              posterRef.current,
              candidatePosterUrl,
              request.match.id,
              request.venue.id,
            );
            if (cancelled) return;
            posterUrl = URL.createObjectURL(posterFile);
          } catch (err) {
            console.error('[share] poster capture failed; falling back to custom only', err);
          }
        }
        if (cancelled) return;
        setPreview({ customUrl, customFile, posterUrl, posterFile });
        // Default to the poster slide when present — that's the upgrade.
        setSelectedSlide(posterFile ? 0 : 1);
      } catch (err) {
        console.error('[share] capture failed', err);
        if (!cancelled) closeRef.current();
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [request]);

  // Escape closes the preview.
  useEffect(() => {
    if (!preview) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [preview]);

  async function confirmShare() {
    if (!preview || !request) return;
    const { customFile, posterFile } = preview;
    const matchup =
      request.match.home && request.match.away
        ? `${request.match.home} ${t('common:vs')} ${request.match.away}`
        : t('common:share2026Fallback');
    const text = t('common:shareGoingText', { venue: request.venue.name, matchup });
    try {
      // Slide 0 = poster composite (when present), Slide 1 = custom card.
      const file = posterFile && selectedSlide === 0 ? posterFile : customFile;
      const result = await sendShare(file, text);
      track(EVENTS.shareCompleted, {
        result,
        matchId: request.match.id,
        venueId: request.venue.id,
      });
      // Fire success only when the share actually went out — `cancelled`
      // means the user backed out of the OS sheet and shouldn't get the
      // "you did the thing" feedback.
      if (result === 'shared' || result === 'downloaded') haptics.success();
    } catch (err) {
      console.error('[share] send failed', err);
    } finally {
      closeRef.current();
    }
  }

  function handleSwipeScroll(e: UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    const clamped = Math.max(0, Math.min(1, idx)) as 0 | 1;
    if (clamped !== selectedSlide) setSelectedSlide(clamped);
  }

  const shareCtaLabel = preview && canNativeShare(preview.customFile) ? t('common:share') : t('common:download');

  return (
    <>
      {request ? (
        <div style={SHARE_HIDDEN_WRAPPER_STYLE} aria-hidden>
          <div ref={shareRef}>
            <ShareCard
              match={request.match}
              venue={request.venue}
              venueCountry={request.venueCountry}
              homeCountry={request.homeCountry}
              awayCountry={request.awayCountry}
            />
          </div>
          {/* PosterCard mounts speculatively whenever the match has a
              candidate poster URL — the capture step skips it if the
              probe came back false. */}
          {posterUrlFor(request.match) && (
            <div ref={posterRef}>
              <PosterCard
                posterImageSrc={posterUrlFor(request.match)!}
                venue={request.venue}
                venueCountry={request.venueCountry}
              />
            </div>
          )}
        </div>
      ) : null}

      {preview ? (
        // Escape-to-close lives on a window-level listener above; the
        // backdrop here only handles outside-click. Disable a11y rules for
        // the click-only backdrop — a fake key handler would be redundant.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('common:sharePreview')}
          style={MODAL_BACKDROP_STYLE}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeRef.current();
          }}
        >
          <div style={MODAL_LABEL_STYLE}>
            {preview.posterUrl
              ? selectedSlide === 0
                ? t('common:sharePreviewPoster')
                : t('common:sharePreviewCustom')
              : t('common:sharePreviewLabel')}
          </div>
          {preview.posterUrl ? (
            <>
              <div
                onScroll={handleSwipeScroll}
                style={SWIPE_CONTAINER_STYLE}
                aria-label={t('common:swipePosterCustom')}
              >
                <img src={preview.posterUrl} alt={t('common:aiPosterAlt')} style={SWIPE_SLIDE_STYLE} draggable={false} />
                <img src={preview.customUrl} alt={t('common:customCardAlt')} style={SWIPE_SLIDE_STYLE} draggable={false} />
              </div>
              <div style={SWIPE_DOTS_STYLE} aria-hidden>
                <div style={selectedSlide === 0 ? SWIPE_DOT_ACTIVE_STYLE : SWIPE_DOT_STYLE} />
                <div style={selectedSlide === 1 ? SWIPE_DOT_ACTIVE_STYLE : SWIPE_DOT_STYLE} />
              </div>
            </>
          ) : (
            <img src={preview.customUrl} alt={t('common:sharePreview')} style={PREVIEW_IMG_STYLE} />
          )}
          <div style={MODAL_BUTTONS_STYLE}>
            <button type="button" style={SECONDARY_BTN_STYLE} onClick={() => closeRef.current()}>
              {t('common:cancel')}
            </button>
            <button type="button" style={PRIMARY_BTN_STYLE} onClick={confirmShare}>
              {shareCtaLabel}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
